import { useCallback, useEffect, useState } from 'react'
import { listSubscriptions } from '@/lib/stripe-gateway'
import * as Sentry from '@sentry/nextjs'
import { useAuth } from '@/lib/hooks/useAuth'
import { ListCustomerSubscriptionItemOut, ProductType } from '../../clients/stripe-gateway'

export function useGetSubscriptions(subscriptionType: ProductType | undefined) {
  const { user } = useAuth()
  const [activeSubscriptions, setActiveSubscriptions] = useState<ListCustomerSubscriptionItemOut[]>([])
  const [inactiveSubscriptions, setInactiveSubscriptions] = useState<ListCustomerSubscriptionItemOut[]>([])
  const [pausedSubscriptions, setPausedSubscriptions] = useState<ListCustomerSubscriptionItemOut[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const getSubscriptions = useCallback(async () => {
    try {
      if (user) {
        const subscriptionsData = await listSubscriptions(user)
        const subscriptions = subscriptionType
          ? subscriptionsData.subscriptions.filter((s) => s.product_type === subscriptionType)
          : subscriptionsData.subscriptions

        const activeSubscriptions = subscriptions.filter((s) => s.status === 'active' && !s.cancellation_date) || null
        const inactiveSubscriptions = subscriptions.filter((s) => s.status === 'ended' || s.cancellation_date)
        const pausedSubscriptions = subscriptions.filter((s) => s.status === 'paused')

        setActiveSubscriptions(activeSubscriptions)
        setInactiveSubscriptions(inactiveSubscriptions)
        setPausedSubscriptions(pausedSubscriptions)

        setLoading(false)
      }
    } catch (e) {
      if (e.response?.status === 403) {
        setActiveSubscriptions([])
        setInactiveSubscriptions([])
        setPausedSubscriptions([])
      } else {
        Sentry.captureMessage('Error in useGetSubscriptions while fetching subscriptions', {
          extra: {
            firebaseUID: user?.uid || 'unknown',
            error: e,
          },
        })
        setError('Error retrieving subscription information. Please logout to try again')
      }
      setLoading(false)
    }
  }, [subscriptionType, user])

  useEffect(() => {
    getSubscriptions()
  }, [user, getSubscriptions])

  return { activeSubscriptions, inactiveSubscriptions, pausedSubscriptions, loading, error, getSubscriptions }
}
